// Every wb_* key a section can carry, assigned to a thematic group. The
// section settings panel (Tools.vue) renders groups in SECTION_GROUP_ORDER
// and shows only the groups a section actually has keys from. A key missing
// from this map lands in 'other' — tests/unit/sectionSettingGroups.spec.ts
// fails the build when that happens, so new keys must be assigned here.

export enum SectionSettingGroup {
  CONTENT = 'content',
  OFFER = 'offer',
  BOOKING_FLOW = 'bookingFlow',
  BOOKING_FORM = 'bookingForm',
  FORM = 'form',
  ACCOUNT = 'account',
  MENU = 'menu',
  COMPANY = 'company',
  IMAGES = 'images',
  MAP = 'map',
  APPEARANCE = 'appearance',
  OTHER = 'other'
}

export const SECTION_GROUP_ORDER: Array<SectionSettingGroup> = [
  SectionSettingGroup.CONTENT,
  SectionSettingGroup.OFFER,
  SectionSettingGroup.BOOKING_FLOW,
  SectionSettingGroup.BOOKING_FORM,
  SectionSettingGroup.FORM,
  SectionSettingGroup.ACCOUNT,
  SectionSettingGroup.MENU,
  SectionSettingGroup.COMPANY,
  SectionSettingGroup.IMAGES,
  SectionSettingGroup.MAP,
  SectionSettingGroup.APPEARANCE,
  SectionSettingGroup.OTHER
]

export const SECTION_GROUP_TITLES: Record<SectionSettingGroup, string> = {
  [SectionSettingGroup.CONTENT]: 'wb_section_group_content',
  [SectionSettingGroup.OFFER]: 'wb_section_group_offer',
  [SectionSettingGroup.BOOKING_FLOW]: 'wb_section_group_booking_flow',
  [SectionSettingGroup.BOOKING_FORM]: 'wb_section_group_booking_form',
  [SectionSettingGroup.FORM]: 'wb_section_group_form',
  [SectionSettingGroup.ACCOUNT]: 'wb_section_group_account',
  [SectionSettingGroup.MENU]: 'wb_section_group_menu',
  [SectionSettingGroup.COMPANY]: 'wb_section_group_company',
  [SectionSettingGroup.IMAGES]: 'wb_section_group_images',
  [SectionSettingGroup.MAP]: 'wb_section_group_map',
  [SectionSettingGroup.APPEARANCE]: 'wb_section_group_appearance',
  [SectionSettingGroup.OTHER]: 'wb_section_group_other'
}

const G = SectionSettingGroup

export const SECTION_SETTING_GROUPS: Record<string, SectionSettingGroup> = {
  wb_title__text__: G.CONTENT,
  wb_title__html_text__: G.CONTENT,
  wb_subtitle__text__: G.CONTENT,
  wb_text__html_text__: G.CONTENT,
  wb_description__html_text__: G.CONTENT,
  wb_section_text__html_text__: G.CONTENT,
  wb_features__array_section_features__: G.CONTENT,
  wb_buttons__array_cta_buttons__: G.CONTENT,
  wb_button__cta_button__: G.CONTENT,
  wb_summary_headline__text__: G.CONTENT,
  wb_summary_text__long_text__: G.CONTENT,
  wb_book_now_label__text__: G.CONTENT,
  wb_service_label__text__: G.CONTENT,
  wb_employee_label__text__: G.CONTENT,
  wb_employee_info_label__text__: G.CONTENT,
  wb_date_label__text__: G.CONTENT,
  wb_selected_services__text__: G.CONTENT,
  wb_select_date_hour__html_text__: G.CONTENT,
  wb_select_date_title_label__html_text__: G.CONTENT,
  wb_select_services_title_label__html_text__: G.CONTENT,
  wb_select_locations_title_label__html_text__: G.CONTENT,
  wb_select_employees_title_label__html_text__: G.CONTENT,
  wb_init_calendar_title__html_text__: G.CONTENT,
  wb_newsletter_input_placeholder__text__: G.CONTENT,
  wb_newsletter_success_message__long_text__: G.CONTENT,
  wb_email_sent__html_text__: G.CONTENT,
  wb_html_code__long_text__: G.CONTENT,
  // Only the Booking1/Booking2 widgets carry this key, where it acts as the
  // widget headline; footers take the real slogan from company data, not
  // from section configuration.
  wb_company_slogan__long_text__: G.CONTENT,

  wb_service_list__services__: G.OFFER,
  wb_employee_list__employees__: G.OFFER,
  wb_only_services_with_subscriptions__checkbox__: G.OFFER,

  wb_selection_order__select__: G.BOOKING_FLOW,
  wb_service_search__checkbox__: G.BOOKING_FLOW,
  wb_location_search__checkbox__: G.BOOKING_FLOW,
  wb_employee_search__checkbox__: G.BOOKING_FLOW,
  wb_separate_online_locations__checkbox__: G.BOOKING_FLOW,
  wb_employee_selection_mode__select__: G.BOOKING_FLOW,
  wb_use_booking_cart__checkbox__: G.BOOKING_FLOW,
  wb_show_booked_slots__checkbox__: G.BOOKING_FLOW,
  wb_show_expand_collapse_button__checkbox__: G.BOOKING_FLOW,
  wb_expand_collapse_service_panels__checkbox__: G.BOOKING_FLOW,
  wb_initial_location_select__checkbox__: G.BOOKING_FLOW,
  wb_initial_service_selection__checkbox__: G.BOOKING_FLOW,
  wb_calendar_min_date__date__: G.BOOKING_FLOW,
  wb_calendar_max_date__date__: G.BOOKING_FLOW,
  wb_start_calendar_time__time__: G.BOOKING_FLOW,
  wb_end_calendar_time__time__: G.BOOKING_FLOW,
  wb_interval_minutes__number__: G.BOOKING_FLOW,
  wb_count_places_in_dates__checkbox__: G.BOOKING_FLOW,
  wb_show_service_description__checkbox__: G.BOOKING_FLOW,
  wb_show_service_column__checkbox__: G.BOOKING_FLOW,
  wb_hide_booking_employees__checkbox__: G.BOOKING_FLOW,
  wb_hide_booking_time_to__checkbox__: G.BOOKING_FLOW,
  wb_hide_time_zone__checkbox__: G.BOOKING_FLOW,

  // The booking form (calendar and booking sections) and the contact/signup
  // form never appear in the same section, so they get separate groups with
  // separate names.
  wb_booking_dynamic_form__dynamic_form__: G.BOOKING_FORM,
  wb_show_name_surname__checkbox__: G.BOOKING_FORM,
  wb_show_phone_number__checkbox__: G.BOOKING_FORM,
  wb_require_phone_number__checkbox__: G.BOOKING_FORM,
  wb_show_billing_data__checkbox__: G.BOOKING_FORM,
  wb_require_billing_data__checkbox__: G.BOOKING_FORM,

  wb_form__dynamic_form__: G.FORM,
  wb_gdpr_fields__gdpr_fields__: G.FORM,
  wb_email_subject__text__: G.FORM,
  wb_email_form_success_url__url__: G.FORM,
  wb_form_position__select__: G.FORM,

  wb_login_modal__external_configuration__: G.ACCOUNT,
  wb_sign_up_modal__external_configuration__: G.ACCOUNT,
  wb_forgotten_password_modal__external_configuration__: G.ACCOUNT,
  wb_forgotten_password_confirmation_modal__external_configuration__: G.ACCOUNT,
  wb_sign_in_button__cta_button__: G.ACCOUNT,
  wb_sign_up_button__cta_button__: G.ACCOUNT,
  wb_show_sign_in_button__checkbox__: G.ACCOUNT,
  wb_show_sign_up_button__checkbox__: G.ACCOUNT,
  wb_show_remind_password_link__checkbox__: G.ACCOUNT,
  wb_redirect_after_login__page_id__: G.ACCOUNT,
  wb_redirect_after_signup__page_id__: G.ACCOUNT,
  wb_hide_login__checkbox__: G.ACCOUNT,
  wb_hide_signup__checkbox__: G.ACCOUNT,

  wb_page_list__page_ids__: G.MENU,
  wb_menu_column_1__page_ids__: G.MENU,
  wb_menu_column_2__page_ids__: G.MENU,
  wb_menu_title__text__: G.MENU,
  wb_main_pages_number__number__: G.MENU,
  wb_more_pages_button__text__: G.MENU,
  wb_redirect_logo__page_id__: G.MENU,
  // The heading of the footer's client-account link column: the twin of
  // wb_menu_title__text__, so it lives in the same group.
  wb_account_title__text__: G.MENU,

  wb_company_name__text__: G.COMPANY,
  wb_show_company_name__checkbox__: G.COMPANY,
  wb_company_phone__text__: G.COMPANY,
  wb_company_email__text__: G.COMPANY,
  wb_company_address__text__: G.COMPANY,
  wb_facebook__url__: G.COMPANY,
  wb_instagram__url__: G.COMPANY,
  wb_youtube__url__: G.COMPANY,
  wb_twitter__url__: G.COMPANY,
  wb_linkedin__url__: G.COMPANY,
  wb_tiktok__url__: G.COMPANY,

  wb_image_hide__checkbox__: G.IMAGES,
  wb_image_hide_on_mobile__checkbox__: G.IMAGES,
  wb_image_contain__checkbox__: G.IMAGES,
  wb_image_width__number__: G.IMAGES,
  wb_image_height__number__: G.IMAGES,
  wb_image_rounded__select__: G.IMAGES,
  wb_image_position__select__: G.IMAGES,
  wb_image_margin_bottom__number__: G.IMAGES,
  wb_image_zoom_on_click__checkbox__: G.IMAGES,
  wb_image_to_right__checkbox__: G.IMAGES,
  wb_gallery_style__select__: G.IMAGES,
  wb_gallery_width__number__: G.IMAGES,
  wb_gallery_height__number__: G.IMAGES,
  wb_gallery_distance__number__: G.IMAGES,
  wb_gallery_image_number__number__: G.IMAGES,
  wb_gallery_mobile_image_number__number__: G.IMAGES,

  wb_latitude__number__: G.MAP,
  wb_longitude__number__: G.MAP,
  wb_zoom__number__: G.MAP,
  wb_map_maker_hide__checkbox__: G.MAP,

  wb_height__style_height__: G.APPEARANCE,
  wb_container_fluid__checkbox__: G.APPEARANCE,
  wb_invert_colors__checkbox__: G.APPEARANCE,
  wb_dark_mode__checkbox__: G.APPEARANCE,
  wb_color_primary__color__: G.APPEARANCE,
  wb_color_bg__color__: G.APPEARANCE,
  wb_color_bg_2__color__: G.APPEARANCE,
  wb_color_text__color__: G.APPEARANCE,
  wb_color_text_2__color__: G.APPEARANCE,
  wb_color_input_border__color__: G.APPEARANCE,
  wb_elevation__number__: G.APPEARANCE,
  wb_align_center__checkbox__: G.APPEARANCE,
  wb_view_flat__checkbox__: G.APPEARANCE,
  wb_view_rounded__select__: G.APPEARANCE,
  wb_button_size__select__: G.APPEARANCE,
  wb_buttons_position__select__: G.APPEARANCE,
  wb_hide_delimiters__checkbox__: G.APPEARANCE,
  wb_show_active_button__checkbox__: G.APPEARANCE,
  wb_hide_arrows__checkbox__: G.APPEARANCE,
  wb_cycle__checkbox__: G.APPEARANCE,
  wb_interval__number__: G.APPEARANCE,
  wb_cols_cols__cols_cols__: G.APPEARANCE,
  wb_cols_sm__cols_sm__: G.APPEARANCE,
  wb_cols_md__cols_md__: G.APPEARANCE,
  wb_cols_lg__cols_lg__: G.APPEARANCE,
  wb_cols_xl__cols_xl__: G.APPEARANCE,
  wb_column_width__number__: G.APPEARANCE,
  wb_text_margin_bottom__number__: G.APPEARANCE,
  wb_text_hide__checkbox__: G.APPEARANCE,
  wb_hide_header__checkbox__: G.APPEARANCE,
  wb_hide_footer__checkbox__: G.APPEARANCE
}

export function sectionSettingGroupForKey (key: string): SectionSettingGroup {
  return SECTION_SETTING_GROUPS[key] || SectionSettingGroup.OTHER
}
